import { IBrandResource } from "../../brands";
import { EntityTypes } from "../../EntityTypes";
import { IFrameworkResource } from "../../frameworks";
import { TrainingActionType } from "../../training-actions";
import { createApiMutationHook, IApiMutationFunction } from "../../utils/api";
import { mapRelationalValue, mapRelationalValueAsIRI, RelationalValue } from "../../utils/filters";
import { ITrainingTemplateResource, TrainingTemplateStatus } from "../types";

export interface ITrainingTemplateUpdateMutationParameters {
    trainingTemplate: RelationalValue<ITrainingTemplateResource>;
    description?: string | null;
    jobFramework?: RelationalValue<IFrameworkResource> | null;
    specificFrameworks?: RelationalValue<IFrameworkResource>[];
    brands?: RelationalValue<IBrandResource>[];
    transversalSkillsFramework?: boolean;
    actions?: Array<{
        type: TrainingActionType;
        duration: number;
        calendarUnit?: "month" | "day" | "hour";
        pedagogicalHours: number;
        description?: string;
    }>;
    status?: TrainingTemplateStatus;
    totalDurationDays?: number;
}

export type ITrainingTemplateUpdateMutationResult = ITrainingTemplateResource;

export const trainingTemplateUpdateMutation: IApiMutationFunction<ITrainingTemplateUpdateMutationParameters> = ({
    trainingTemplate,
    description,
    jobFramework,
    specificFrameworks,
    brands,
    transversalSkillsFramework,
    actions,
    status,
    totalDurationDays,
}) => {
    return {
        method: "PATCH",
        url: `/training_templates/${mapRelationalValue(trainingTemplate)}`,
        headers: {
            "Content-Type": "application/merge-patch+json",
        },
        data: {
            ...(description !== undefined && { description }),
            ...(jobFramework !== undefined && {
                jobFramework: jobFramework ? mapRelationalValueAsIRI(EntityTypes.FRAMEWORK, jobFramework) : null,
            }),
            ...(specificFrameworks !== undefined && {
                specificFrameworks: specificFrameworks.map(framework =>
                    mapRelationalValueAsIRI(EntityTypes.FRAMEWORK, framework),
                ),
            }),
            ...(brands !== undefined && {
                brands: brands.map(brand => mapRelationalValueAsIRI(EntityTypes.BRAND, brand)),
            }),
            ...(transversalSkillsFramework !== undefined && { transversalSkillsFramework }),
            ...(actions !== undefined && {
                trainingActions: actions.map(action => ({
                    type: action.type,
                    calendarDuration: action.duration,
                    calendarUnit: action.calendarUnit ?? "month",
                    pedagogicalHours: action.pedagogicalHours,
                    description: action.description,
                })),
            }),
            ...(status !== undefined && { status }),
            ...(totalDurationDays !== undefined && { totalDurationDays }),
        },
    };
};

export const useTrainingTemplateUpdateMutation = createApiMutationHook<
    ITrainingTemplateUpdateMutationParameters,
    ITrainingTemplateUpdateMutationResult
>(trainingTemplateUpdateMutation);
